/**
 * The console's second factor, from the client side.
 *
 * Status says whether Google Authenticator is set up and whether this
 * session has verified it lately. After a successful code the token is
 * refreshed so the new claim reaches the next call at once.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '../../lib/callable';
import { useAuth } from '../../auth/useAuth';

export interface MfaStatus {
  enrolled: boolean;
  verified: boolean;
  verifiedUntil: number | null;
  lockedUntil: number | null;
}

export const MFA_STATUS_KEY = ['console-mfa-status'] as const;

/** How long until the status must be asked again: just past the end of the verification, or never when there is none. */
export function recheckIn(status: MfaStatus | undefined, now: number): number | false {
  if (!status?.verified || typeof status.verifiedUntil !== 'number') return false;
  // setTimeout cannot hold more than about 24 days; a verification is twelve hours, so this only guards a bad value.
  return Math.min(Math.max(status.verifiedUntil - now + 1000, 1000), 2_000_000_000);
}

export function useMfaStatus(enabled: boolean) {
  return useQuery({
    queryKey: MFA_STATUS_KEY,
    enabled,
    retry: false,
    staleTime: 60_000,
    /**
     * A verification stands for twelve hours, and a page can stay open longer
     * than that. Asked once and never again, the gate went on showing the
     * application while the server had stopped honoring the role, and a
     * System Admin was told "This action requires admin role or higher" on
     * their own Organization page (2026-10-06). So the status is asked again
     * the moment the verification runs out, and whenever the window is
     * returned to, because a sleeping laptop does not run timers.
     */
    refetchInterval: (query) => recheckIn(query.state.data, Date.now()),
    refetchOnWindowFocus: 'always',
    queryFn: () => call<Record<string, never>, MfaStatus>('sysAdminMfaStatus', {}),
  });
}

export function useMfaBeginEnroll() {
  return useMutation({
    mutationFn: () => call<Record<string, never>, { secret: string; otpauthUrl: string }>('sysAdminMfaBeginEnroll', {}),
  });
}

function useAfterVerify() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  return async () => {
    await refresh();
    await qc.invalidateQueries({ queryKey: MFA_STATUS_KEY });
  };
}

export function useMfaConfirmEnroll() {
  const after = useAfterVerify();
  return useMutation({
    mutationFn: (code: string) => call<{ code: string }, { verifiedUntil: number }>('sysAdminMfaConfirmEnroll', { code }),
    onSuccess: after,
  });
}

export function useMfaVerify() {
  const after = useAfterVerify();
  return useMutation({
    mutationFn: (code: string) => call<{ code: string }, { verifiedUntil: number }>('sysAdminMfaVerify', { code }),
    onSuccess: after,
  });
}
